/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2025 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, {
  CSSProperties,
  FC,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import {
  FormModal,
  IDiagnosticReportContent,
  IPatientResponse,
  OptionTypeBase,
  SelectChangeFunction,
  BlockUI,
  ContentType,
  FileType,
  DiagnosticReportFooter,
  getReportContentByType,
  DiagnosticReportHeader,
  getDiagnosticReportOptions,
  DocumentViewer,
  DiagnosticReportButtons,
  convertKBToMB,
  DEFAULT_TIMEZONE,
  LIMIT_REPORT_MB_SIZE,
  ViewDiagnosticReportMode,
  IStudyDetail,
  PERMISSIONS,
} from "@advapacs/common";
import { useSelector } from "react-redux";
import { OnChangeValue } from "react-select";
import { useTranslation } from "react-i18next";
import { useFlag } from "@unleash/proxy-client-react";
import { IRootState } from "@src/config/reducers";
import { downloadPdf, fetchReportDataContent } from "./ducks/operators";
import { useAppDispatch } from "@src/config/store";
import useOpenMeddream from "@src/hooks/viewers/useOpenMeddream";
import useOpenEUnity from "@src/hooks/viewers/useOpenEUnity";
import useOpenAdvapacsViewer from "@src/hooks/viewers/useOpenAdvapacsViewer";
import { hasPermission } from "@src/utils/utils";
import { isIOS } from "react-device-detect";
import useTrackAdvapacsViewerLaunchClick from "@src/hooks/useTrackAdvapacsViewerLaunchClick";
import { Account } from "../login/ducks/slices";
import { orderApi, StudyApi } from "@src/config/api";
import { axiosInstanceRegion } from "@src/config/axios-region";
import { orderDownloadPdfReport } from "@src/pages/criticalResult/ducks/operations";
import {
  getOrderDiagnosticReport,
  getStudyDiagnosticReport,
  orderFetchReportDataContent,
} from "@src/reducers/operations/diagnosticReportOperations";

export interface IViewDiagnosticReportModal {
  portalId: string;
  patientDetail: IPatientResponse;
  show: boolean;
  accessionNumber?: string;
  studyDate?: Date | string;
  modalities?: Array<string>;
  isSensitive?: boolean;
  viewMode?: ViewDiagnosticReportMode;
  viewModeId?: string; // orderId or StudyId
  hasReport?: boolean;
  studies?: Array<IStudyDetail>; // for view from order
  onClose: () => void;
}

export interface IReportContent {
  [key: string]: string;
}

const ViewDiagnosticReportModal: FC<IViewDiagnosticReportModal> = ({
  show,
  onClose,
  patientDetail,
  portalId,
  hasReport,
  accessionNumber,
  studyDate,
  modalities,
  viewMode,
  viewModeId,
  studies,
}) => {
  /**
   * States and global states
   */
  const { t } = useTranslation("modal");
  const dispatch = useAppDispatch();
  const enabledViewTextMode = useFlag(
    "enabled-view-text-mode-in-report-viewer"
  );
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const { account, features } = useSelector(
    (state: IRootState) => state.loginReducer
  );

  const { reportDetail } = useSelector(
    (state: IRootState) => state.diagnosticReportReducer
  );

  const {
    localisation: { dateTimeFormat, dateFormat },
  } = account as Account;
  const [modalCentered, setModalCentered] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [contentOptions, setContentOptions] = useState<OptionTypeBase[]>();
  const [selectedReportContent, setSelectedReportContent] =
    useState<IDiagnosticReportContent>();
  const [contents, setContents] = useState<IReportContent>({});
  const [hasGenerateReport, setHasGenerateReport] = useState(false);
  const [loading, setLoading] = useState(false);

  const openWithMeddream = useOpenMeddream();
  const openWithEUnity = useOpenEUnity();
  const openWithAdvapacsViewer = useOpenAdvapacsViewer();
  const enabledAdvapacsViewerInCp = useFlag("cp-advapacs-viewer");
  const trackAdvapacsViewerLaunchClick = useTrackAdvapacsViewerLaunchClick(
    "cp-diagnostic-report-viewer"
  );

  const trackedOpenWithEUnity = (ids: string[]) => {
    trackAdvapacsViewerLaunchClick("open-viewer", "eunity");
    openWithEUnity(ids);
  };

  const trackedOpenWithMeddream = (ids: string[]) => {
    trackAdvapacsViewerLaunchClick("open-viewer", "meddream");
    openWithMeddream(ids);
  };

  const hasEUnityFeature =
    studies?.some((study) =>
      features?.some(
        (feature) =>
          feature.clinicianPortal?.id === study.clinicianPortal?.id &&
          feature.eUnity
      )
    ) || false;

  const hasMeddreamFeature =
    studies?.some((study) =>
      features?.some(
        (feature) =>
          feature.clinicianPortal?.id === study.clinicianPortal?.id &&
          feature.medDream
      )
    ) || false;

  // The UI will handle this case itself. If the account is AdvaPACS View-only, the UI will hide EUnity and MedDream.
  const showEUnity = hasEUnityFeature && !account?.advaViewOnly;
  const showMeddream = hasMeddreamFeature && !account?.advaViewOnly;

  const tenantPermissions =
    studies?.flatMap((study) =>
      features
        ?.filter(
          (feature) =>
            feature?.clinicianPortal?.id === study?.clinicianPortal?.id &&
            feature?.permissions?.length
        )
        .flatMap((feature) => feature?.permissions)
    ) || [];

  // Resolves the viewer to open on devices where AdvaView is unsupported (iOS).
  // Meddream takes priority over EUnity when the tenant has both available.
  // Intentionally ignores `advaViewOnly`: on unsupported devices the fallback viewer is still allowed.
  const fallbackViewer = () => {
    const canAccessMeddream =
      hasMeddreamFeature &&
      hasPermission(tenantPermissions as string[], [
        PERMISSIONS.ACCESS_MEDDREAM,
      ]);
    const canAccessEUnity =
      hasEUnityFeature &&
      hasPermission(tenantPermissions as string[], [PERMISSIONS.ACCESS_EUNITY]);

    if (canAccessMeddream) {
      return openWithMeddream;
    }
    if (canAccessEUnity) {
      return openWithEUnity;
    }
    return undefined;
  };

  const advapacsViewerFallback = isIOS
    ? fallbackViewer()
    : openWithAdvapacsViewer;

  /**
   * Main Functions
   * @param option
   */

  const handleDownload = () => {
    if (viewMode === ViewDiagnosticReportMode.ORDER) {
      orderDownloadPdfReport(
        portalId,
        patientDetail.id,
        viewModeId,
        contentOptions?.find(
          (x) => x?.value?.contentType === selectedReportContent?.contentType
        )?.value,
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        isShowPDF && hasGenerateReport
      );
    } else {
      downloadPdf(
        portalId,
        patientDetail.id,
        viewModeId,
        contentOptions?.find(
          (x) => x?.value?.contentType === selectedReportContent?.contentType
        )?.value,
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        isShowPDF && hasGenerateReport
      );
    }
  };

  const handleSelectChange: SelectChangeFunction = async (
    option: OnChangeValue<OptionTypeBase, false>
  ) => {
    setSelectedReportContent(option?.value);
  };

  const getContentStyle = useMemo((): CSSProperties => {
    if (!iframeRef?.current || hasGenerateReport) {
      return {
        maxHeight: window.innerHeight - 400,
        height: window.innerHeight - 400,
        overflowY: "auto",
      };
    }
    return {
      maxHeight: window.innerHeight - 400,
      height:
        (iframeRef?.current?.contentDocument?.body?.scrollHeight || 0) >
        window.innerHeight / 2
          ? window.innerHeight - 400
          : (iframeRef?.current?.contentDocument?.body?.scrollHeight || 0) +
            100,
      overflowY: "auto",
    };
  }, [hasGenerateReport]);

  useEffect(() => {
    // call the diagnostic report content. the return content like pdf, or html.
    if (patientDetail?.id && viewModeId && hasReport) {
      if (viewMode === ViewDiagnosticReportMode.ORDER) {
        dispatch(
          getOrderDiagnosticReport(portalId, patientDetail.id, viewModeId)
        );
      } else {
        dispatch(
          getStudyDiagnosticReport(portalId, patientDetail.id, viewModeId)
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientDetail?.id, portalId, viewModeId, viewMode, hasReport]);

  useEffect(() => {
    if (reportDetail) {
      const report = getDiagnosticReportOptions(
        reportDetail,
        enabledViewTextMode
      );
      setHasGenerateReport(report.isGeneratePdfReport);
      setContentOptions(report.options);
      let defaultValue = report.options.filter(
        (c) => c.label === FileType.PDF
      )[0];
      if (!defaultValue) defaultValue = report.options[0];
      setSelectedReportContent(defaultValue?.value);
    }
  }, [reportDetail, enabledViewTextMode]);

  const loadStudyDefaultContent = async (
    currentPortalId: string,
    patientId: string,
    currentViewModeId: string,
    reportContent: IDiagnosticReportContent,
    report: boolean
  ) => {
    try {
      setLoading(true);
      const result = await fetchReportDataContent(
        currentPortalId,
        patientId,
        currentViewModeId,
        reportContent,
        report
      );
      const newContents = { ...contents, [reportContent.tag]: result };
      setContents(newContents);
      setModalCentered(false);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  // load data of order report content.
  const loadOrderDefaultContent = async (
    currentPortalId: string,
    patientId: string,
    currentViewModeId: string,
    reportContent: IDiagnosticReportContent,
    report: boolean
  ) => {
    try {
      setLoading(true);
      const result = await orderFetchReportDataContent(
        currentPortalId,
        patientId,
        currentViewModeId,
        reportContent,
        report
      );
      const newContents = { ...contents, [reportContent.tag]: result };
      setContents(newContents);
      setModalCentered(false);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      selectedReportContent &&
      selectedReportContent.id &&
      contents[selectedReportContent.tag] === undefined
    ) {
      const fileConverted = convertKBToMB(selectedReportContent?.size);
      const isFileLarge = fileConverted > LIMIT_REPORT_MB_SIZE;
      const isDPFFile =
        selectedReportContent?.tag?.toLowerCase() === ContentType.PDF_TAG;
      const shouldDontCallAPI = isFileLarge && isDPFFile;
      if (shouldDontCallAPI) return;
      // check size here.
      if (viewMode === ViewDiagnosticReportMode.STUDY) {
        loadStudyDefaultContent(
          portalId,
          patientDetail?.id,
          viewModeId,
          selectedReportContent,
          hasReport
        );
      } else {
        loadOrderDefaultContent(
          portalId,
          patientDetail?.id,
          viewModeId,
          selectedReportContent,
          hasReport
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contents,
    patientDetail?.id,
    selectedReportContent,
    portalId,
    hasGenerateReport,
  ]);

  /**
   * Memo
   */

  const reportPDFSize = useMemo(
    () => convertKBToMB(selectedReportContent?.size),
    [selectedReportContent]
  );

  const isShowHTMLOrTextPlain = useMemo(() => {
    return (
      selectedReportContent &&
      selectedReportContent?.contentType !== ContentType.APPLICATION_PDF
    );
  }, [selectedReportContent]);

  const isShowPDF = useMemo(() => {
    return (
      selectedReportContent &&
      selectedReportContent?.contentType === ContentType.APPLICATION_PDF &&
      (contents[selectedReportContent.tag] !== undefined ||
        reportPDFSize > LIMIT_REPORT_MB_SIZE)
    );
  }, [selectedReportContent, contents, reportPDFSize]);

  const isShowDownloadButton = useMemo(() => {
    return contentOptions?.length > 0;
  }, [contentOptions]);

  const pdfURL = useMemo(() => {
    const condition =
      !reportDetail?.generatePdf &&
      selectedReportContent?.tag === ContentType.PDF_TAG;
    let path = "";
    switch (viewMode) {
      case ViewDiagnosticReportMode.STUDY:
        path = condition
          ? StudyApi.GET_DIAGNOSTIC_REPORT_CONTENT(
              portalId,
              patientDetail?.id,
              viewModeId,
              selectedReportContent?.id
            )
          : StudyApi.GENERATE_PDF_REPORT(
              portalId,
              patientDetail?.id,
              viewModeId,
              selectedReportContent?.id
            );
        break;

      case ViewDiagnosticReportMode.ORDER:
        path = orderApi.ORDER_GENERATE_PDF_REPORT_PDF(
          portalId,
          patientDetail?.id,
          viewModeId,
          selectedReportContent?.id
        );
        break;
      default:
        break;
    }

    const params: Record<string, string> = {
      origin: window.origin,
      "x-tz": DEFAULT_TIMEZONE.value,
    };

    const urlParams = new URLSearchParams(params);
    const url = `${axiosInstanceRegion.defaults.baseURL}${path}`;
    return condition ? url : `${url}?${urlParams.toString()}`;
  }, [
    patientDetail,
    portalId,
    selectedReportContent,
    viewMode,
    viewModeId,
    reportDetail,
  ]);

  const openNewTab = async () => {
    try {
      const response = await axiosInstanceRegion.get(pdfURL, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {}
  };

  return (
    <>
      <FormModal
        show={show}
        size="lg"
        header={{
          text: t("view_diagnostic_report"),
        }}
        cancelBtnName={t("close")}
        loading={false}
        onClose={onClose}
        cancel={onClose}
        centered={modalCentered}
        isShowDownloadBtn={isShowDownloadButton}
        footerElement={<DiagnosticReportFooter reportDetail={reportDetail} />}
        download={handleDownload}
      >
        <DiagnosticReportHeader
          reportDetail={reportDetail}
          dateTimeFormat={dateTimeFormat}
          dateFormat={dateFormat}
          studyDate={studyDate}
          accessionNumber={accessionNumber}
          issuerEnabled={true}
          modalities={modalities}
          patientDetail={patientDetail}
        />
        <hr className="m-tb-xs" />
        <DiagnosticReportButtons
          openWithEUnity={trackedOpenWithEUnity}
          openWithMeddream={trackedOpenWithMeddream}
          openWithAdvapacsViewer={advapacsViewerFallback}
          onAdvapacsViewerLaunchClick={() =>
            trackAdvapacsViewerLaunchClick("open-viewer")
          }
          handleSelectChange={handleSelectChange}
          isSensitive={false}
          canShowSensitive={true}
          permissions={tenantPermissions}
          contentOptions={contentOptions}
          eUnityEnabled={showEUnity}
          medDreamEnabled={showMeddream}
          isShowAdvapacsViewer={
            enabledAdvapacsViewerInCp && (!isIOS || !!advapacsViewerFallback)
          }
          viewerEnabled
          advapacsViewerPermission={PERMISSIONS.VIEW_STUDY}
          selectedReportContent={selectedReportContent}
          studies={studies}
        />
        {/* Report Content */}
        <div className="form-group row mb-0">
          <div className="col-lg-12">
            <div className="card">
              <div
                style={!modalCentered ? getContentStyle : { height: "auto" }}
                className={
                  !(
                    selectedReportContent && contents[selectedReportContent.tag]
                  )
                    ? "card-body"
                    : ""
                }
              >
                <BlockUI isBlocked={loading || (isShowPDF && !pdfLoaded)}>
                  <>
                    {isShowHTMLOrTextPlain && (
                      <DocumentViewer
                        type="HTML"
                        srcDoc={getReportContentByType(
                          contents[selectedReportContent.tag],
                          selectedReportContent?.contentType
                        )}
                      />
                    )}

                    {isShowPDF && (
                      <DocumentViewer
                        type="PDF"
                        src={contents[selectedReportContent?.tag]}
                        isLargestFile={
                          convertKBToMB(selectedReportContent?.size) >
                          LIMIT_REPORT_MB_SIZE
                        }
                        setPdfLoaded={setPdfLoaded}
                        openNewTab={() => {
                          if (
                            !reportDetail?.generatePdf &&
                            selectedReportContent?.tag === ContentType.PDF_TAG
                          ) {
                            openNewTab();
                          } else {
                            window.open(pdfURL, "_blank");
                          }
                        }}
                      />
                    )}
                  </>
                </BlockUI>
              </div>
            </div>
          </div>
        </div>
      </FormModal>
    </>
  );
};

// @ts-ignore
export default ViewDiagnosticReportModal;
